import React from "react";
import { DatePicker, Alert, Descriptions, Tag } from "antd";
import dayjs from "dayjs";
import { Button, Col, Form, Row, Typography } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Moment from "moment";
import { updateCard } from "../../api/AdminRequest";
import UpdateSlug from "./UpdateSlug";
import Features from "./Features";
import UpdateQR from "./UpdateQR";

const { Title } = Typography;

function CardEdit({ edit }) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const cardData = state?.cardData || {};

  const [form] = Form.useForm();

  // Check if admin is expired
  const isAdminExpired =
    cardData.expiryMessage === "Admin Expired" ||
    cardData.expiryMessage?.toLowerCase().includes("admin expired");

  // Calculate actual expiry date
  let calculatedExpiryDate = null;
  if (cardData?.expiryInfo?.createdDate && cardData?.expiryInfo?.expireDays) {
    const createdDate = dayjs(cardData.expiryInfo.createdDate);
    calculatedExpiryDate = createdDate.add(
      cardData.expiryInfo.expireDays,
      "days",
    );
  }

  const onFinish = async (values) => {
    try {
      await updateCard(cardData._id, values);

      toast.success("Card expiry date updated successfully", {
        position: "top-right",
        autoClose: 3000,
        theme: "dark",
      });

      setTimeout(() => {
        navigate("/all-cards");
      }, 3000);
    } catch (error) {
      console.log(error.message);
      toast.error(error.message || "Failed to update card");
    }
  };

  const handleEditAdmin = () => {
    navigate("/edit-admin", {
      state: {
        drawerData: cardData.userID?.adminID, // Pass the admin/user data
      },
    });
  };

  return (
    <>
      <Row gutter={16} className="mb-4">
        <Col span={24} className="flex justify-between items-center">
          <Title level={2}>Card Details</Title>
          <Button onClick={() => navigate("/all-cards")}>
            Back to All Cards
          </Button>
        </Col>
      </Row>

      {/* Error Alert for Admin Expired */}
      {isAdminExpired && (
        <Alert
          message="Admin Expired"
          description={
            cardData.expiryMessage ||
            "This card's admin has expired. Please update the admin's expiry date to reactivate the card."
          }
          type="error"
          showIcon
          action={
            <Button
              size="small"
              type="primary"
              danger
              onClick={handleEditAdmin}
            >
              Edit Admin
            </Button>
          }
          className="mb-4"
        />
      )}

      {/* Expiry Warning */}
      {!isAdminExpired && cardData.isExpired && (
        <Alert
          message="Card Expired"
          description={cardData.expiryMessage || "This card has expired."}
          type="warning"
          showIcon
          className="mb-4"
        />
      )}

      <div className="flex justify-center overflow-x-auto">
        <div className="w-full">
          {/* Card Information (Read-only) */}
          <Descriptions
            bordered
            column={2}
            title="Card Information (Read Only)"
            className="mb-6"
          >
            <Descriptions.Item label="Card ID">
              {cardData._id || "N/A"}
            </Descriptions.Item>

            <Descriptions.Item label="Slug">
              {cardData.slug || "N/A"}
            </Descriptions.Item>

            <Descriptions.Item label="Owner Name">
              {cardData.name || "N/A"}
            </Descriptions.Item>

            <Descriptions.Item label="Email">
              {cardData.email || "N/A"}
            </Descriptions.Item>

            <Descriptions.Item label="Phone">
              {cardData.phone || "N/A"}
            </Descriptions.Item>

            <Descriptions.Item label="User Type">
              <Tag
                color={
                  cardData.userID?.userType === "admin-user"
                    ? "blue"
                    : "default"
                }
              >
                {cardData.userID?.userType || "N/A"}
              </Tag>
            </Descriptions.Item>

            <Descriptions.Item label="Card Type">
              <Tag color="purple">{cardData.cardType || "Business Card"}</Tag>
            </Descriptions.Item>

            <Descriptions.Item label="Status">
              <Tag color={cardData.status === "active" ? "success" : "error"}>
                {cardData.status === "active" ? "Active" : "Blocked"}
              </Tag>
            </Descriptions.Item>

            <Descriptions.Item label="Expiry Status">
              <Tag color={cardData.isExpired ? "red" : "green"}>
                {cardData.expiryMessage || "Active"}
              </Tag>
            </Descriptions.Item>

            <Descriptions.Item label="Created At" span={2}>
              {cardData.createdAt
                ? Moment(cardData.createdAt).format("MMMM Do YYYY, h:mm a")
                : "N/A"}
            </Descriptions.Item>

            {cardData.expiryInfo && (
              <>
                <Descriptions.Item label="Current Expiry Days">
                  {cardData.expiryInfo.expireDays} days
                </Descriptions.Item>

                <Descriptions.Item label="Current Expiry Date">
                  <span
                    className={
                      cardData.isExpired ? "text-red-500 font-bold" : ""
                    }
                  >
                    {calculatedExpiryDate
                      ? calculatedExpiryDate.format("MMMM Do YYYY, h:mm a")
                      : "N/A"}
                  </span>
                </Descriptions.Item>
              </>
            )}
          </Descriptions>

          {/* Editable Expiry Date Form */}
          <Form
            form={form}
            name="editCardExpiry"
            onFinish={onFinish}
            layout="vertical"
            initialValues={{
              expiryDays: cardData.expiryInfo?.expireDays || null,
            }}
          >
            <Title level={4}>Update Card Expiry</Title>
            <p className="text-gray-500 mb-4">
              Only the expiry date can be modified
            </p>

            <Form.Item
              name="expiryDays"
              label={
                <span className="text-lg font-semibold">New Expiry Date</span>
              }
              rules={[
                {
                  required: true,
                  message: "Please select a new expiry date!",
                },
              ]}
              getValueFromEvent={(date) => {
                if (!date) return null;
                // Calculate days from card's created date
                const cardCreated = dayjs(
                  cardData.expiryInfo?.createdDate || cardData.createdAt,
                );
                return Math.ceil(date.diff(cardCreated, "days", true));
              }}
              getValueProps={(value) => ({
                value:
                  value && cardData.expiryInfo?.createdDate
                    ? dayjs(cardData.expiryInfo.createdDate).add(value, "days")
                    : null,
              })}
            >
              <DatePicker
                placeholder="Select New Expiry Date"
                disabledDate={(current) => {
                  const cardCreated = dayjs(
                    cardData.expiryInfo?.createdDate || cardData.createdAt,
                  );
                  return current && current < cardCreated.startOf("day");
                }}
                className="py-2"
                style={{ width: "100%" }}
                size="large"
              />
            </Form.Item>

            <Form.Item>
              <div className="flex gap-2">
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Update Expiry Date
                </Button>
                <Button onClick={() => navigate("/all-cards")} size="large">
                  Cancel
                </Button>
              </div>
            </Form.Item>
          </Form>
          <UpdateSlug cardId={cardData._id} initialSlug={cardData.slug} />
          <Features cardId={cardData._id} cardData={cardData} />
          <UpdateQR cardId={cardData._id} />
        </div>
      </div>

      <ToastContainer />
    </>
  );
}

export default CardEdit;
